import type { Metric } from 'web-vitals/base';

import { YCBConfigArray } from 'Config/types';
import { DISTANCE_UNIT_KILOMETER, DISTANCE_UNIT_MILE } from 'Lib/utils/distance';

import { DistanceUnit } from 'Store/appSettings/types';

// font used on front page
import proximanovaBold from 'Assets/fonts/proximanova-bold.woff2';
import proximanovaMedium from 'Assets/fonts/proximanova-medium.woff2';
import proximanovaRegular from 'Assets/fonts/proximanova-regular.woff2';
import proximanovaSemiBold from 'Assets/fonts/proximanova-semibold.woff2';

import { BUGSNAG_ID } from './constants/bugsnag';
import { help, instagram, lite, tinder } from './constants/domain';
import supportedLanguages from './languages';

// For Adyen and Braintree - use dev key if on dev env, and prod if pointed at prod
const ADYEN_ENCRYPT_DEV_KEY =
  '10001|BA007593573053C253E7A75A6410B8E23C1497099F7DA08B76A763F15B9D578D8709A4E868DE7ACC471BF52B016E08983137C83049445833F0F46B616FE2A7AD976CBE8F0DF2BD307B3F8563DEF550059EC9A1E430E334E3091724552CB176371DFC81587E652EEAC1324309C7D122C9C535984D3500972DC3C60F5804ACA5B88A8E112B4714C505157DD4E3A2D8CBF5895D55943F66D20BE7C2372F4A10B0424B0B9A17B6A71C02A5A00E993E59B110D4C5A245FB38AFC3E20786A707B9494DBE922DB51749488776B4EBE2A3A9D5EC31BD28E470990474C848F47831F7DE9252CE0D16C145A77AFFF3D82BDE6912DF69C3D47CAA61258FCE209723C9FE8C2B';
const ADYEN_ENCRYPT_PROD_KEY =
  '10001|F1BDE9ED76A50A619BE7431D11749051A56171E203E6E3F1475A646B0E4CE744EDCC0DD8713F2D09953F9D7BBF43EECFA43450FDA195D7876A5FFE7EB375197CA45BCFBD4A3EE9177FF1429A388AD5982F8DF8F4662E9E8B673F2605AC658F58E12A060F155DEC5EBA07EFB14527781C07B89067F4C8683A08CAFD383211B4C556F8F07AEBF50D5945C17DF23DB2116A4E743C3860250496867C6ED7EE56A9AC9347B7EAA16AD7E72E8C4E206739FD6F82968EF6D703FE6DF89B427E3C3BC4CE74D6AEE0A24B175CEC738F145D46613B03E91C8237E9950B23C51BF6702FF8DF25305F8C8E20A71076BF7564515897EB7237507C7DFCC9CFC81BFA746145A8E3';
let ADYEN_ENCRYPT_KEY = ADYEN_ENCRYPT_PROD_KEY;
// Assign the right Adyen key for local development, staging and production deployments
if (
  (__DEVELOPMENT__ && process.env.SERVER_API !== 'https://api.gotinder.com') ||
  process.env.DEPLOY_ENV === 'prodtest'
) {
  ADYEN_ENCRYPT_KEY = ADYEN_ENCRYPT_DEV_KEY;
}

const RECAPTCHA_DEV_KEY = '6Le76BMUAAAAABUOuizzs6CoYjawHNeAVBmo4VuV';
const ARKOSE_BAN_APPEAL_DEV_KEY = '4A1B21B6-21A2-C367-7F0E-5919A51FD93A';
const FACEBOOK_PIXEL_ID = '323130149577519';
const TIKTOK_PIXEL_ID = 'CD30BTJC77U35BVUO550';
const SNAP_PIXEL_ID = '08dba6cd-89bf-4141-ac48-3c633561c585';
const PINTEREST_PIXEL_ID = '2612780557801';
const GOOGLE_PIXEL_ID = 'AW-856138155/3uiPCKnWgOYDEKvDnpgD';

const PUSH_STAGING_KEY = [
  0x04, 0x6a, 0xfa, 0xbf, 0x25, 0x6d, 0x4a, 0x63, 0x87, 0xa6, 0x7a, 0xaf, 0x9c, 0x6c, 0x20, 0xd8,
  0x3e, 0x66, 0x9c, 0x0b, 0x2d, 0x24, 0x48, 0xb6, 0x42, 0x81, 0xc6, 0xb7, 0x17, 0x0b, 0xf4, 0xfc,
  0x41, 0xa0, 0xf1, 0xfd, 0x53, 0x3e, 0x2f, 0x62, 0x81, 0xf5, 0x1e, 0xfc, 0x39, 0x9a, 0x78, 0xcd,
  0xc5, 0xfd, 0x77, 0xd8, 0x77, 0xc2, 0x13, 0xd4, 0xb3, 0x10, 0x85, 0x99, 0x9a, 0xf3, 0x75, 0xbc,
  0x61,
]; // eslint-disable-line max-len
const PUSH_PROD_KEY = [
  0x04, 0x00, 0x35, 0xa7, 0x9a, 0xab, 0x4e, 0x8c, 0xc2, 0xe5, 0xab, 0xd3, 0xc2, 0x67, 0x77, 0xa4,
  0x38, 0x90, 0xb2, 0xeb, 0x9e, 0x43, 0xde, 0x75, 0x7e, 0x1b, 0x4f, 0xc6, 0x83, 0x35, 0xed, 0x6c,
  0xb4, 0xb5, 0x9a, 0xdc, 0x9c, 0x66, 0x39, 0x06, 0x61, 0xcd, 0x2e, 0xcd, 0xc3, 0x5a, 0x46, 0x70,
  0xf1, 0x51, 0xb4, 0x15, 0x57, 0x11, 0x85, 0xe9, 0x9c, 0xa3, 0xa6, 0xbb, 0x67, 0xed, 0x36, 0x42,
  0x63,
]; // eslint-disable-line max-len

const MASTER_FEATURES = {
  analyticsProtobufEvents: 0,
  analyticsValidateProtobuf: 0,
  arkoseGuestAuth: 0,
  avif: 0,
  consumables: 1,
  cookiePolicy: 1,
  darkModeInitial: 0,
  devTools: 0,
  enableAnalytics: 1,
  giftCard: 0,
  gold: 1,
  incentiveRedemption: 1,
  lite: 0,
  liteHomepage: 0,
  matchmaker: 1,
  matchmakerSuperLikeOffer: 0,
  modernBuild: 0,
  mutualFriends: 0,
  platinum: 1,
  redirectOnRestricted: 0,
  relationshipIntent: 0,
  remote: 0,
  remoteRevenueView: 0,
  skipLoadingScreen: 0,
  skipLocationWatcher: 0,
  skipPostLogin: 0,
  stacksUtil: 0,
  tabWatch: 1,
  tinderPlus: 1,
  tinderSelectOnboarding: 1,
  tinderSelectCheckout: 1,
  videoChat: 0,
  webp: 0,
  webPayment: 1, // Default value
  websocketActive: 1,
  webProfileShareField: 0,
};

export type Feature = keyof typeof MASTER_FEATURES;

type IdleTrackerConfig = {
  enabled: 0 | 1;
  timeout: number;
};

export type AppConfig = {
  sessionActivityThrottle: number;
  sessionTimeout: number;
  updateDelay: number;
  customGender: string[];
  siteUrl: string;
  sharedUrl: string;
  imageProxyURL: string;
  fallbackUrl: string;
  fbSDK: string;
  helpForm: {
    prefix: string;
    url: string;
    redirect: string;
    allowlist: string[];
    devicePlatform: 'web' | 'lite';
  };
  authAgeDisclaimer: boolean;
  fbPermissionsScope: string;
  idMaskKey: string;
  adyen: {
    encryptKey: string;
  };
  analytics: {
    appsflyer: string;
    facebook: string;
    gtag: string;
  };
  gaPropertyID: string;
  gCrossMediaFloodlightID?: string;
  googleAdsRemarketingID?: string;
  supportedLanguages: typeof supportedLanguages;
  distanceUnit: DistanceUnit;
  termsUpdateVersionPrefix: {
    usVersionPrefix: string;
    internationalVersionPrefix: string;
  };
  facebookAppSettings: {
    appId: string;
    version: string;
  };
  instagramSettings: {
    url: string;
    popupWidth: number;
    popupHeight: number;
  };
  lineAppSettings: {
    lineOAuthUrl: string;
    lineRedirectUri: string;
    popupWidth: number;
    popupHeight: number;
    responseType: string;
    scope: string;
  };
  recaptcha: {
    scriptUrl: string;
    siteKey: string;
  };
  arkoseCaptcha: {
    baseUrl: string;
    apiScriptUrl: string;
    onboardingKey: string;
    banAppealKey: string;
  };
  tinderPlusRoutes: string[];
  GoogleAuth: {
    apiUrl: string;
    clientId: string;
  };
  bugsnagID: string;
  facebookPixelID: string;
  tiktokPixelID: string;
  snapchatPixelID: string;
  pinterestPixelID: string;
  googlePixelID: string;
  spotify: {
    authEndpoint: string;
    clientId: string;
    popupHeight: number;
    popupWidth: number;
    redirectUri: string;
    scope: string;
    trackUrl: string;
  };
  numberOfConversationStarters: number;
  i13n: {
    rootModelData: {
      app: string;
      platform: number;
      dataProvider: string;
    };
  };
  perf: {
    enabled: 0 | 1;
    beacon: Record<Metric['name'], string>;
    apiSamplingRate: number;
  };
  discoverySettings: {
    ageMax: number;
    ageMin: number;
    ageInfinite: number;
    distanceMin: number;
    distanceMax: number;
    distanceMaxKm: number;
    numPhotosMax: number;
    numPhotosMin: number;
  };
  sparks: {
    endpoint: string;
    v3endpoint: string;
    isDebugBuild: boolean;
    platform: number;
    requestEndpointValidation: boolean;
  };
  shortIdleTracker: IdleTrackerConfig;
  longIdleTracker: IdleTrackerConfig;
  experiments: string[];
  features: Record<Feature, 0 | 1>;
  gifs: {
    limit: number;
  };
  passport: {
    libId: string;
    libUri: string;
    apiKey: string;
    libraries: string;
  };
  typingIndicator: {
    pulse: number;
    ttl: number;
  };
  defaultSupportedCards: string[];
  websocket: {
    reconnectJitter: number;
    baseRetryInterval: number;
    maxRetryInterval: number;
    pingInterval: number;
    pingTimeout: number;
    disconnectOnIdle: boolean;
  };
  logServerResponseTime: 0 | 1;
  session: {
    params: string[];
    ttl: number;
  };
  ui: {
    inputDebounceTime: number;
    profileImageMax: number;
    profileImagePerPage: number;
  };
  lite: {
    intent: string;
    package: string;
    referrer: string;
    maxVersion: number;
  };
  reviewLink?: string;
  privacyDefaults: {
    advertising: boolean;
    analytics: boolean;
    social: boolean;
  };
  privacyMaxDays: number;
  profile: {
    help: {
      whyAmIHidden: string;
    };
  };
  ageVerification: {
    supportedPlatformsAndDevices: string;
  };
  selfieChallengeV2: {
    howDoesPhotoVerificationWorkURL: string;
    learnMore: string;
  };
  selfieVerification: {
    helpContact: string;
    learnMore: string;
  };
  userVerification: {
    idAndPhotoVerification: string;
  };
  mutualFriends: {
    learnMore: string;
  };
  verification: {
    youtube: string;
  };
  preloadFonts: string[];
  openTok: {
    apiKey: string;
  };
  accountManagementPortal: string;
  pushPublicKey: number[];
  gotinderRedirectRoutes: string[];
};

const app: YCBConfigArray<AppConfig> = [
  {
    settings: ['main'] as const,
    sessionActivityThrottle: 30 * 1000,
    sessionTimeout: 12 * 60 * 1000,
    updateDelay: 2000,
    customGender: [],
    siteUrl: tinder,
    sharedUrl: tinder,
    imageProxyURL: 'https://images-ssl.gotinder.com',
    fallbackUrl: help,
    fbSDK: 'https://connect.facebook.net/en_US/sdk.js',
    helpForm: {
      prefix: 'hf',
      url: `${help}/hc/requests/new`,
      redirect: `${help}/hc`,
      allowlist: ['fbe', 'd', 'os', 'ti', 'u', 'm', 'loc', 'p', 'ph', 'g'],
      devicePlatform: 'web',
    },
    authAgeDisclaimer: false,
    fbPermissionsScope: 'user_birthday,user_photos,email,user_likes',
    // random uuid
    idMaskKey: '768e07b4-db28-40d1-bacd-6c8c21546ca8',
    adyen: {
      encryptKey: ADYEN_ENCRYPT_KEY,
    },
    analytics: {
      facebook: 'https://connect.facebook.net/en_US/fbevents.js',
      appsflyer: 'https://onelinksmartscript.appsflyer.com/onelink-smart-script-latest.js',
      gtag: 'https://www.googletagmanager.com/gtag/js',
    },
    // for dev environments, overriden in production + lite
    gaPropertyID: 'UA-60214108-4',
    supportedLanguages,
    distanceUnit: DISTANCE_UNIT_KILOMETER,
    termsUpdateVersionPrefix: {
      usVersionPrefix: 'US_TOS',
      internationalVersionPrefix: '',
    },
    facebookAppSettings: {
      appId: '464891386855067',
      version: 'v2.8',
    },
    instagramSettings: {
      url: instagram,
      popupWidth: 500,
      popupHeight: 565,
    },
    recaptcha: {
      scriptUrl: 'https://www.google.com/recaptcha/api.js?onload=onGrecaptchaLoad&render=explicit',
      siteKey: '6Le66BMUAAAAAF4o3FmzHED0keQLefMYKhePAyrv',
    },
    arkoseCaptcha: {
      baseUrl: 'https://tinder-api.arkoselabs.com/v2',
      apiScriptUrl: 'api.js',
      onboardingKey: '15485B1D-E5A1-0166-162C-FF8EBF968955',
      banAppealKey: 'EBC0462E-1FD4-25CD-A21E-A68A0E5DDB23',
    },
    tinderPlusRoutes: [
      '/app/product/subscription/plus',
      '/app/product/subscription/gold',
      '/app/product/subscription/platinum',
      '/app/checkout',
      '/app/settings/update-card/checkout',
      '/app/settings/plus',
      '/app/settings/plus/passport',
      '/app/settings/gold',
      '/app/settings/platinum',
    ],
    GoogleAuth: {
      apiUrl: 'https://accounts.google.com/gsi/client',
      clientId: '230402993429-g4nobau40t3v3j0tvqto4j8f35kil4hf.apps.googleusercontent.com',
    },
    bugsnagID: BUGSNAG_ID,
    facebookPixelID: FACEBOOK_PIXEL_ID,
    tiktokPixelID: TIKTOK_PIXEL_ID,
    snapchatPixelID: SNAP_PIXEL_ID,
    pinterestPixelID: PINTEREST_PIXEL_ID,
    googlePixelID: GOOGLE_PIXEL_ID,
    spotify: {
      authEndpoint: 'https://accounts.spotify.com/authorize',
      clientId: 'b06a803d686e4612bdc074e786e94062',
      popupHeight: 565,
      scope:
        'playlist-read-private playlist-read-collaborative streaming user-library-read user-read-private user-top-read',
      popupWidth: 500,
      redirectUri: '/app/spotify/authenticate',
      trackUrl: 'https://open.spotify.com/track/',
    },
    numberOfConversationStarters: 89, // translation starts from "conversationStarter"
    i13n: {
      rootModelData: {
        app: 'TinderWeb',
        platform: 4,
        dataProvider: '', // no current way to distinguish wifi vs ethernet
      },
    },
    profile: {
      help: {
        whyAmIHidden: 'https://www.help.tinder.com/hc/articles/26151863213837',
      },
    },
    perf: {
      enabled: 0,
      beacon: {
        // Cumulative Layout Shift (CLS) https://web.dev/cls/
        CLS: 'onCLS',
        // First Contentful Paint https://web.dev/fcp/
        FCP: 'onFCP',
        // First Input Delay https://web.dev/fid/
        FID: 'onFID',
        // Largest Contentful Paint https://web.dev/lcp/
        LCP: 'onLCP',
        // Time To First Byte https://web.dev/time-to-first-byte/
        TTFB: 'onTTFB',
        // Interaction to Next Paint (INP) https://web.dev/inp/
        INP: 'onINP',
      },
      apiSamplingRate: 0.01,
    },
    discoverySettings: {
      ageMax: 100,
      ageMin: 18,
      ageInfinite: 1000,
      distanceMin: 1,
      distanceMax: 100,
      distanceMaxKm: 161,
      numPhotosMax: 9,
      numPhotosMin: 1,
    },
    sparks: {
      endpoint: 'https://messagepublish.ue1.d4.tstaging.com/v2/batch/event',
      v3endpoint: 'https://messagepublish.ue1.d4.tstaging.com/v3/publish/app/json',
      isDebugBuild: true,
      platform: 4,
      requestEndpointValidation: true,
    },
    shortIdleTracker: {
      enabled: 1,
      timeout: 30 * 1000, // 30 seconds
    },
    longIdleTracker: {
      enabled: 1,
      timeout: 30 * 60 * 1000, // 30 minutes
    },
    lineAppSettings: {
      lineRedirectUri: '/app/line/authenticate',
      lineOAuthUrl: 'https://access.line.me/oauth2/v2.1/authorize',
      responseType: 'code',
      scope: 'openid',
      popupWidth: 500,
      popupHeight: 565,
    },
    experiments: ['auth_options', 'sms_auth_v2', 'two_factor_auth'],
    features: MASTER_FEATURES as Record<Feature, 0 | 1>,
    gifs: {
      limit: 40,
    },
    passport: {
      libId: 'google-maps-api',
      libUri: 'https://maps.googleapis.com/maps/api/js',
      apiKey: 'AIzaSyA0KCtfu2lU5sim6CyK_JWIinzqOSBOw7w',
      libraries: 'places',
    },
    typingIndicator: {
      pulse: 2000,
      ttl: 5000,
    },
    defaultSupportedCards: ['Visa', 'Mastercard'],
    websocket: {
      reconnectJitter: 0.2,
      baseRetryInterval: 1000,
      maxRetryInterval: 10000,
      pingInterval: 60000,
      pingTimeout: 90000,
      disconnectOnIdle: true,
    },
    logServerResponseTime: 1,
    session: {
      params: ['tchannel', 'tsource', 'tcampaign', 'tad', 'cpv1', 'cpv2'],
      ttl: 14400000, // 4 hours
    },
    ui: {
      inputDebounceTime: 500,
      profileImageMax: 9, // https://gotinder.atlassian.net/browse/TDP-169869
      profileImagePerPage: 9,
    },
    lite: {
      intent: 'tinderlite://',
      package: 'com.tinder.tinderlite',
      referrer: 'android-app://com.tinder.tinderlite',
      maxVersion: 5, // Android version
    },
    privacyDefaults: {
      advertising: false,
      analytics: false,
      social: false,
    },
    privacyMaxDays: 180,
    ageVerification: {
      supportedPlatformsAndDevices: `${help}/hc/articles/115003245686-Supported-platforms-and-devices`,
    },
    selfieChallengeV2: {
      howDoesPhotoVerificationWorkURL: `${help}/hc/en-us/articles/4422771431309-How-Does-Photo-Verification-Work`,
      learnMore: `${help}/hc/articles/26147205007885`,
    },
    selfieVerification: {
      helpContact: `${help}/hc/articles/360034307072-Trouble-with-Photo-Verification-`,
      learnMore: `${help}/hc/articles/360033058071`,
    },
    userVerification: {
      idAndPhotoVerification: `${help}/hc/articles/19868368795917-What-is-ID-Photo-Verification-`,
    },
    mutualFriends: {
      learnMore: `${help}/hc/articles/7511247800333`,
    },
    verification: {
      youtube: 'QDIYEHbWnQr6CHseVU5UNrQYlcTf34CbLoP1wnvbOQ8',
    },
    preloadFonts: [proximanovaBold, proximanovaRegular, proximanovaRegular, proximanovaSemiBold],
    openTok: {
      apiKey: '46688612',
    },
    accountManagementPortal: 'https://account.gotinder.com',
    pushPublicKey: PUSH_STAGING_KEY,
    gotinderRedirectRoutes: [
      '/careers',
      '/contact',
      '/festivalmode',
      '/festivalmodeau',
      '/help',
      '/refunds',
      '/tindercares',
      '/zlabs',
    ],
  },
  {
    settings: ['environment:production'],
    sparks: {
      endpoint: 'https://data.gotinder.com/v2/batch/event',
      v3endpoint: 'https://data.gotinder.com/v3/publish/app/json',
      isDebugBuild: false,
      requestEndpointValidation: false,
    },
    gaPropertyID: 'UA-60214108-5',
    gCrossMediaFloodlightID: 'DC-9799045', // Cross Media Vendor tracking https://gotinder.atlassian.net/browse/TDP-61077
    perf: {
      enabled: 1,
    },
    pushPublicKey: PUSH_PROD_KEY,
    features: {
      arkoseGuestAuth: 1,
    },
    logServerResponseTime: 0,
  },
  {
    settings: ['country:JP'],
    authAgeDisclaimer: true,
  },
  {
    // Always block sanctioned countries
    settings: ['environment:production', 'country:CU,KP,IR,SD,SY'],
    features: {
      redirectOnRestricted: 1,
    },
  },
  // Tunisia, Ghana, & Benin are blocked due to chargeback issues: TDP-24943
  // Saudi Arabia blocked due to burdensome tax reporting law: TDP-25530
  // United Arab Emirates and Bahrain: https://github.com/TinderBackend/config/pull/2439
  // Taiwan due to purchase receipt regulation (Legal) https://github.com/TinderBackend/config/pull/3039
  {
    settings: ['environment:production', 'country:TN,GH,BJ,SA,AE,BH,TW'],
    features: {
      tinderPlus: 0,
      consumables: 0,
      gold: 0,
      platinum: 0,
    },
  },
  {
    settings: ['country:GB,US,AU,CA,BR,JP,MX,FR,ES,IN,VN,TH,ID,KR,DE'],
    features: {
      matchmakerSuperLikeOffer: 1,
    },
  },
  {
    settings: ['country:US,MM,BU,LR,GB'],
    distanceUnit: DISTANCE_UNIT_MILE,
  },
  {
    settings: ['webp:1'],
    features: {
      webp: 1,
    },
  },
  {
    settings: ['avif:1'],
    features: {
      avif: 1,
    },
  },
  {
    settings: ['lite:1'],
    features: {
      consumables: 0,
      gold: 0,
      incentiveRedemption: 0,
      lite: 1,
      liteHomepage: 1,
      platinum: 0,
      webPayment: 0,
      tabWatch: 0,
      tinderPlus: 0,
    },
    reviewLink: 'https://play.google.com/store/apps/details?id=com.tinder.tinderlite',
    gaPropertyID: 'UA-60214108-13',
    helpForm: {
      devicePlatform: 'lite',
    },
    siteUrl: lite,
    i13n: {
      rootModelData: {
        platform: 5,
      },
    },
    sparks: {
      platform: 5,
    },
    preloadFonts: [proximanovaMedium, proximanovaRegular, proximanovaSemiBold],
  },
  {
    settings: ['modern:1'],
    features: {
      modernBuild: 1,
    },
  },
  {
    settings: ['standalone:1'],
    features: {
      liteHomepage: 1,
      tabWatch: 0,
    },
    preloadFonts: [proximanovaMedium, proximanovaRegular, proximanovaSemiBold],
  },
  {
    settings: ['remote:1'],
    features: {
      remote: 1,
      cookiePolicy: 0,
      enableAnalytics: 0,
      remoteRevenueView: 1,
      skipLoadingScreen: 1,
      tabWatch: 0,
      skipLocationWatcher: 1,
    },
  },
  {
    settings: ['prerender:1'],
    features: {
      cookiePolicy: 0,
      tabWatch: 0,
    },
  },
  {
    settings: ['skynet:1'],
    features: {
      devTools: 1,
      giftCard: 1,
      matchmaker: 1,
      webPayment: 1,
      webProfileShareField: 1,
    },
  },
  {
    settings: ['country:IN'],
    customGender: ['Hijra', 'Kothi'], // extra gender
  },
  {
    settings: ['country:BR'],
    googleAdsRemarketingID: 'AW-429098540',
    features: {
      webProfileShareField: 1,
    },
  },
  {
    settings: ['country:US'],
    features: {
      giftCard: 1,
    },
  },
  {
    settings: ['experience:matchmaker'],
    features: {
      skipPostLogin: 1,
      skipLocationWatcher: 1,
    },
  },
  {
    settings: ['experience:select'],
    features: {
      skipPostLogin: 1,
      skipLocationWatcher: 1,
    },
  },
  {
    settings: ['experience:gift'],
    features: {
      skipPostLogin: 1,
      skipLocationWatcher: 1,
    },
  },
];

if (__TEST__) {
  app.push({
    settings: ['environment:test'],
    recaptcha: {
      siteKey: '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI',
    },
    distanceUnit: DISTANCE_UNIT_MILE,
    features: {
      analyticsProtobufEvents: 1,
      analyticsValidateProtobuf: 1,
    },
  });
}

if (__DEVELOPMENT__) {
  app.push({
    settings: ['environment:development'],
    updateDelay: 10000,
    recaptcha: {
      siteKey: RECAPTCHA_DEV_KEY,
    },
    // arkoseCaptcha: {
    //   banAppealKey: ARKOSE_BAN_APPEAL_DEV_KEY,
    //   SMSKey: 'DFF92730-3F54-4FFE-87F9-ABD0C882FD83'
    // },
    features: {
      analyticsProtobufEvents: 1,
      analyticsValidateProtobuf: 1,
      devTools: 1,
      darkModeInitial: 1,
      giftCard: 1,
      matchmaker: 1,
      mutualFriends: 1,
      relationshipIntent: 1,
      stacksUtil: 1,
      videoChat: 1,
      websocketActive: 0,
      webProfileShareField: 1,
    },
    facebookAppSettings: {
      appId: '410609353191710',
    },
    preloadFonts: [],
  });
}

if (__PRODTEST__) {
  app.push({
    settings: ['environment:prodtest'],
    recaptcha: {
      siteKey: RECAPTCHA_DEV_KEY,
    },
    arkoseCaptcha: {
      banAppealKey: ARKOSE_BAN_APPEAL_DEV_KEY,
    },
    perf: {
      enabled: 1,
    },
    features: {
      analyticsProtobufEvents: 1,
      analyticsValidateProtobuf: 1,
      darkModeInitial: 1,
      devTools: 1,
      giftCard: 1,
      relationshipIntent: 1,
      stacksUtil: 1,
    },
  });
}

export default app;
